import { fetch_with_timeout } from '../helpers';
import {
	build_data_hub_action_privacy_headers,
	data_hub_privacy_policy_allows_browser_storage
} from '../helpers/data_hub_privacy_policy';
import { build_browser_context } from './browser';
import { build_page_metadata } from './metadata';
import { sanitize_json_compatible_value } from './schema';
import { analytics_captures_disabled } from './state';

export const WEB_ANALYTICS_POST_PATH = '/api/post/web';
const WEB_ANALYTICS_POST_TIMEOUT_MS = 8000;
// Matches data-hub-action routes/post_web.js MAX_EVENTS_PER_REQUEST.
const WEB_ANALYTICS_MAX_EVENTS_PER_FLUSH = 100;
const pending_events_storage_key_prefix = 'data-hub:web:pending-events';

function get_persisted_pending_events_storage_key(data_hub_instance) {
	const organization_id = typeof data_hub_instance?.organization_id === 'string' && data_hub_instance.organization_id.trim()
		? data_hub_instance.organization_id.trim()
		: 'shared';
	return `${pending_events_storage_key_prefix}:${organization_id}`;
}

function read_persisted_pending_events_from_storage(data_hub_instance) {
	if (!data_hub_privacy_policy_allows_browser_storage(data_hub_instance)) {
		return [];
	}
	if (typeof localStorage === 'undefined') {
		return [];
	}
	try {
		const raw_value = localStorage.getItem(get_persisted_pending_events_storage_key(data_hub_instance));
		if (!raw_value) {
			return [];
		}
		const parsed_value = JSON.parse(raw_value);
		return Array.isArray(parsed_value) ? parsed_value : [];
	} catch {
		return [];
	}
}

function write_persisted_pending_events_to_storage(data_hub_instance, pending_events) {
	if (!data_hub_privacy_policy_allows_browser_storage(data_hub_instance)) {
		return;
	}
	if (typeof localStorage === 'undefined') {
		return;
	}
	try {
		const storage_key = get_persisted_pending_events_storage_key(data_hub_instance);
		if (!Array.isArray(pending_events) || pending_events.length === 0) {
			localStorage.removeItem(storage_key);
			return;
		}
		localStorage.setItem(storage_key, JSON.stringify(pending_events));
	} catch {
		// Storage may be blocked in private or restricted contexts.
	}
}

function trim_persisted_events_to_limits(pending_events, data_hub_instance) {
	const maximum_event_count = data_hub_instance?.web_settings?.max_persisted_events || 30;
	const maximum_byte_size = data_hub_instance?.web_settings?.max_persisted_bytes || 32 * 1024;
	let trimmed_events = pending_events.slice(-maximum_event_count);
	while (trimmed_events.length > 0) {
		try {
			if (JSON.stringify(trimmed_events).length <= maximum_byte_size) {
				break;
			}
		} catch {
			trimmed_events.shift();
			continue;
		}
		trimmed_events.shift();
	}
	return trimmed_events;
}

function merge_events_deduped_by_web_event_uuid(existing_events, incoming_events) {
	const merged_events_by_uuid = new Map();
	(existing_events || []).forEach((analytics_event) => {
		if (analytics_event?.web_event_uuid) {
			merged_events_by_uuid.set(analytics_event.web_event_uuid, analytics_event);
		}
	});
	(incoming_events || []).forEach((analytics_event) => {
		if (analytics_event?.web_event_uuid) {
			merged_events_by_uuid.set(analytics_event.web_event_uuid, analytics_event);
		}
	});
	return Array.from(merged_events_by_uuid.values());
}

function requeue_failed_flush_events(analytics_state, events_to_flush) {
	if (!Array.isArray(events_to_flush) || events_to_flush.length === 0) {
		return;
	}
	analytics_state.queue = Array.isArray(analytics_state.queue) ? analytics_state.queue : [];
	analytics_state.queue.unshift(...events_to_flush);
}

function clean_event_for_delivery(analytics_event) {
	const delivered_event = { ...(analytics_event || {}) };
	delete delivered_event.__queued_at_ms;
	delivered_event.client_queue_duration_ms = Math.max(
		0,
		Date.now() - (analytics_event?.__queued_at_ms || Date.now())
	);
	Object.keys(delivered_event).forEach((key) => {
		if (delivered_event[key] === undefined) {
			delete delivered_event[key];
		}
	});
	return delivered_event;
}

/**
 * Appends failed or unload-at-risk events to localStorage so a later page load can retry.
 * Respects max_persisted_events / max_persisted_bytes and dedupes on web_event_uuid.
 *
 * @param {Object} data_hub_instance
 * @param {Array<Object>} analytics_events
 * @returns {number} Count of events now stored for retry.
 */
export function persist_failed_events(data_hub_instance, analytics_events = []) {
	if (analytics_captures_disabled(data_hub_instance) || !data_hub_instance?.web_settings?.enabled) {
		return 0;
	}
	const deliverable_events = (Array.isArray(analytics_events) ? analytics_events : [])
		.filter((analytics_event) => analytics_event?.event_name && analytics_event?.web_event_uuid);
	if (!deliverable_events.length) {
		return 0;
	}
	const merged_events = merge_events_deduped_by_web_event_uuid(
		read_persisted_pending_events_from_storage(data_hub_instance),
		deliverable_events
	);
	const trimmed_events = trim_persisted_events_to_limits(merged_events, data_hub_instance);
	write_persisted_pending_events_to_storage(data_hub_instance, trimmed_events);
	return trimmed_events.length;
}

/**
 * Moves persisted retry events into the in-memory queue and clears localStorage.
 * Called once during init_analytics before the first flush.
 *
 * @param {Object} data_hub_instance
 * @returns {number} Count of events merged into the queue.
 */
export function drain_persisted_analytics_events(data_hub_instance) {
	if (analytics_captures_disabled(data_hub_instance) || !data_hub_instance?.web_settings?.enabled) {
		return 0;
	}
	const persisted_events = read_persisted_pending_events_from_storage(data_hub_instance);
	if (!persisted_events.length) {
		return 0;
	}
	write_persisted_pending_events_to_storage(data_hub_instance, []);
	const analytics_state = data_hub_instance.analytics_state || {};
	analytics_state.queue = Array.isArray(analytics_state.queue) ? analytics_state.queue : [];
	analytics_state.queue = merge_events_deduped_by_web_event_uuid(analytics_state.queue, persisted_events);
	return persisted_events.length;
}

function remove_delivered_events_from_persisted_storage(data_hub_instance, delivered_events) {
	if (!Array.isArray(delivered_events) || delivered_events.length === 0) {
		return;
	}
	const delivered_uuid_set = new Set(
		delivered_events.map((analytics_event) => analytics_event?.web_event_uuid).filter(Boolean)
	);
	if (!delivered_uuid_set.size) {
		return;
	}
	const remaining_events = read_persisted_pending_events_from_storage(data_hub_instance)
		.filter((analytics_event) => !delivered_uuid_set.has(analytics_event?.web_event_uuid));
	write_persisted_pending_events_to_storage(data_hub_instance, remaining_events);
}

/**
 * Builds the browser-to-/api/post/web request body. This intentionally omits loose user_data;
 * when the backend is wired up it will extract identity only from cookie/body JWTs and stamp
 * server-side fields. Exported so callers and tests can inspect the wire payload shape even
 * for tests and debugging without performing a network call.
 */
export function build_web_event_request_body(data_hub_instance, analytics_events, delivery_type = 'fetch') {
	const delivered_events = Array.isArray(analytics_events)
		? analytics_events.map(clean_event_for_delivery).filter((event) => event?.event_name && event?.web_event_uuid)
		: [];
	const first_event_metadata = delivered_events.find((event) => event.metadata)?.metadata || {};
	return {
		organization_id: data_hub_instance?.organization_id || null,
		jwt: data_hub_instance?.jwt_cookie || data_hub_instance?.organization_jwt || null,
		delivery_type,
		web_schema_version: 1,
		data_hub_js_version: typeof window !== 'undefined' && window.DataHub?.version ? String(window.DataHub.version) : null,
		metadata: build_page_metadata(data_hub_instance, first_event_metadata),
		browser: build_browser_context(data_hub_instance),
		web_event_data: data_hub_instance?.web_event_data && typeof data_hub_instance.web_event_data === 'object'
			? sanitize_json_compatible_value(data_hub_instance.web_event_data)
			: {},
		events: delivered_events
	};
}

export function queue_analytics_event(data_hub_instance, analytics_event) {
	if (!analytics_event || !data_hub_instance?.web_settings?.enabled) {
		return null;
	}
	data_hub_instance.analytics_state.queue = Array.isArray(data_hub_instance.analytics_state.queue)
		? data_hub_instance.analytics_state.queue
		: [];
	data_hub_instance.analytics_state.queue.push(analytics_event);
	return analytics_event;
}

async function flush_analytics_events_batch(data_hub_instance, options = {}) {
	if (analytics_captures_disabled(data_hub_instance)) {
		return { ok: false, skipped: true, error_type: 'captures_disabled' };
	}
	if (!data_hub_instance?.web_settings?.enabled) {
		if (data_hub_instance?.analytics_state) {
			data_hub_instance.analytics_state.queue = [];
			data_hub_instance.analytics_state.pre_init_queue = [];
		}
		return { ok: false, skipped: true };
	}
	const analytics_state = data_hub_instance.analytics_state || {};
	const queued_events = Array.isArray(analytics_state.queue) ? analytics_state.queue : [];
	if (!queued_events.length) {
		return { ok: true, skipped: true };
	}

	const events_to_flush = queued_events.splice(
		0,
		Math.min(queued_events.length, WEB_ANALYTICS_MAX_EVENTS_PER_FLUSH)
	);
	const delivery_type = options.is_unload ? 'keepalive_fetch' : 'fetch';
	const request_body = build_web_event_request_body(data_hub_instance, events_to_flush, delivery_type);

	if (!data_hub_instance?.forms_domain) {
		requeue_failed_flush_events(analytics_state, events_to_flush);
		persist_failed_events(data_hub_instance, events_to_flush);
		return { ok: false, delivery_type, error_type: 'missing_forms_domain' };
	}

	const post_url = `${data_hub_instance.forms_domain}${WEB_ANALYTICS_POST_PATH}`;
	const headers = {
		'Content-Type': 'application/json',
		...build_data_hub_action_privacy_headers(data_hub_instance)
	};
	const body_string = JSON.stringify(request_body);

	if (options.is_unload) {
		// Persist before keepalive so browsers that drop the in-flight request still
		// recover on the next page load. Rare duplicate rows in BQ are acceptable;
		// dedupe on web_event_uuid downstream if needed.
		persist_failed_events(data_hub_instance, events_to_flush);
		try {
			fetch(post_url, {
				method: 'POST',
				headers,
				credentials: 'include',
				body: body_string,
				keepalive: true
			}).catch((error) => {
				data_hub_instance?.error_function?.(
					'POST web analytics keepalive flush failed: ' + (error?.message || error),
					null
				);
			});
		} catch (error) {
			data_hub_instance?.error_function?.(
				'POST web analytics keepalive flush threw: ' + (error?.message || error),
				null
			);
			return { ok: false, delivery_type, error_type: 'keepalive_fetch_error' };
		}
		return { ok: true, delivery_type, skipped_await: true };
	}

	try {
		const response = await fetch_with_timeout(post_url, {
			method: 'POST',
			headers,
			credentials: 'include',
			body: body_string,
			timeout: WEB_ANALYTICS_POST_TIMEOUT_MS,
			abort_on_timeout: false
		});
		if (!response?.ok) {
			data_hub_instance?.error_function?.(
				`POST web analytics non-OK response status ${response?.status || 'unknown'}`,
				null
			);
			requeue_failed_flush_events(analytics_state, events_to_flush);
			persist_failed_events(data_hub_instance, events_to_flush);
			return { ok: false, status: response?.status || null, delivery_type, error_type: 'http_error' };
		}
		remove_delivered_events_from_persisted_storage(data_hub_instance, events_to_flush);
		return { ok: true, status: response.status, delivery_type };
	} catch (error) {
		data_hub_instance?.error_function?.(
			'POST web analytics fetch failed: ' + (error?.message || error),
			null
		);
		requeue_failed_flush_events(analytics_state, events_to_flush);
		persist_failed_events(data_hub_instance, events_to_flush);
		return { ok: false, delivery_type, error_type: 'network_error' };
	}
}

/**
 * Drains queued analytics events and POSTs them to data-hub-action's /api/post/web endpoint.
 * Concurrent callers share one in-flight flush; normal flushes chain until the queue is empty.
 *
 * @param {Object} data_hub_instance - DataHub instance or compatible object.
 * @param {Object} options - Delivery options. { is_unload: true } sends via keepalive fetch
 *     (fire-and-forget) so the request survives navigation; the function returns immediately
 *     without awaiting the response.
 * @returns {Promise<Object>} Result object — shape varies by path:
 *   - unload: { ok: true, delivery_type, skipped_await: true } (network sent, not awaited)
 *   - normal: { ok, status, delivery_type, error_type? }
 */
export async function flush_analytics_events(data_hub_instance, options = {}) {
	const analytics_state = data_hub_instance?.analytics_state || {};
	if (analytics_state.flush_in_progress_promise) {
		return analytics_state.flush_in_progress_promise;
	}

	analytics_state.flush_in_progress_promise = (async () => {
		let last_result = { ok: true, skipped: true };
		do {
			last_result = await flush_analytics_events_batch(data_hub_instance, options);
			if (options.is_unload || last_result.skipped || !last_result.ok) {
				break;
			}
		} while ((data_hub_instance?.analytics_state?.queue || []).length > 0);
		return last_result;
	})().finally(() => {
		analytics_state.flush_in_progress_promise = null;
	});

	return analytics_state.flush_in_progress_promise;
}

export { get_persisted_pending_events_storage_key };
